import React, { useEffect, useState } from 'react';
import BaseLayout from './BaseLayout';
import "../App.css";
import axios from 'axios';
import { useNavigate, Link } from 'react-router-dom';
import { API_BASE } from '../api';
import { useAuth } from '../context/AuthContext';
import FlameLoader from './FlameLoader';
import ParticlesBg from './ParticlesBg';

const NFL_TEAMS = [
    "Arizona Cardinals", "Atlanta Falcons", "Baltimore Ravens", "Buffalo Bills",
    "Carolina Panthers", "Chicago Bears", "Cincinnati Bengals", "Cleveland Browns",
    "Dallas Cowboys", "Denver Broncos", "Detroit Lions", "Green Bay Packers",
    "Houston Texans", "Indianapolis Colts", "Jacksonville Jaguars", "Kansas City Chiefs",
    "Las Vegas Raiders", "Los Angeles Chargers", "Los Angeles Rams", "Miami Dolphins",
    "Minnesota Vikings", "New England Patriots", "New Orleans Saints", "New York Giants",
    "New York Jets", "Philadelphia Eagles", "Pittsburgh Steelers", "San Francisco 49ers",
    "Seattle Seahawks", "Tampa Bay Buccaneers", "Tennessee Titans", "Washington Commanders"
];

const NBA_TEAMS = [
    "Atlanta Hawks", "Boston Celtics", "Brooklyn Nets", "Charlotte Hornets",
    "Chicago Bulls", "Cleveland Cavaliers", "Dallas Mavericks", "Denver Nuggets",
    "Detroit Pistons", "Golden State Warriors", "Houston Rockets", "Indiana Pacers",
    "LA Clippers", "Los Angeles Lakers", "Memphis Grizzlies", "Miami Heat",
    "Milwaukee Bucks", "Minnesota Timberwolves", "New Orleans Pelicans", "New York Knicks",
    "Oklahoma City Thunder", "Orlando Magic", "Philadelphia 76ers", "Phoenix Suns",
    "Portland Trail Blazers", "Sacramento Kings", "San Antonio Spurs", "Toronto Raptors",
    "Utah Jazz", "Washington Wizards"
];

const BADGE_META = {
    admin:         { label: 'Admin',            cls: 'badge-admin'        },
    founder:       { label: 'Founder',          cls: 'badge-info'         },
    '1st':         { label: 'Leaderboard: 1st', cls: 'badge-gold'         },
    '2nd':         { label: 'Leaderboard: 2nd', cls: 'badge-silver'       },
    '3rd':         { label: 'Leaderboard: 3rd', cls: 'badge-bronze'       },
    fire_lord:     { label: 'Fire Lord',        cls: 'badge-fire-lord'    },
    goat:          { label: 'GOAT',             cls: 'badge-goat'         },
    legend:        { label: 'Legend',           cls: 'badge-legend'       },
    elite:         { label: 'Elite',            cls: 'badge-elite'        },
    flame_prince:  { label: 'Flame Prince',     cls: 'badge-flame-prince' },
    popular:       { label: 'Popular',          cls: 'badge-popular'      },
    perfectionist: { label: 'Perfectionist',    cls: 'badge-perfectionist'},
    poop:          { label: '💩',               cls: 'badge-poop'         },
    all_rounder:   { label: 'All-Rounder',      cls: 'badge-all-rounder'  },
    traitor:       { label: 'Traitor',          cls: 'badge-traitor'      },
    friendly:      { label: 'Friendly',         cls: 'badge-friendly'     },
    nostalgic:     { label: 'Nostalgic',        cls: 'badge-nostalgic'    },
};

const ACH_TITLES = {
    on_fire_4:          'On Fire IV',
    flame_picker_4:     'Flame Picker IV',
    login_novice:       'FP Novice',
    login_amateur:      'FP Amateur',
    wins_100:           'Winner IV',
    leaderboard_top1:   'The GOAT',
    flame_picker_2:     'Flame Picker II',
    flame_picker_3:     'Flame Picker III',
    leaderboard_top10:  'Elite',
    login_legend:       'FP Legend',
    login_professional: 'FP Pro',
    on_fire_3:          'On Fire III',
    wins_10:            'Winner II',
    wins_50:            'Winner III',
    on_fire_2:          'On Fire II',
};

const BADGE_UNLOCK_ACH = {
    '1st':         null,
    '2nd':         null,
    '3rd':         null,
    founder:       'cis4914',
    fire_lord:     'on_fire_4',
    goat:          'leaderboard_top1',
    legend:        'login_legend',
    elite:         'leaderboard_top10',
    flame_prince:  'on_fire_3',
    popular:       'social_4',
    perfectionist: 'perfect_day',
    poop:          'loss_streak_10',
    all_rounder:   'all_leagues',
    traitor:       'betrayal',
    friendly:      'social_2',
    nostalgic:     'classic_mode',
};

const SectionBody = ({ isOpen, children }) => (
    <div className={`ap-section-body${isOpen ? ' ap-section-body--open' : ''}`}>
        <div className="ap-section-body-inner">{children}</div>
    </div>
);

const EditProfilePage = () => {
    const { authData, updateUserAttribute } = useAuth();
    const [loading, setLoading] = useState(true);
    const [profilePicError, setProfilePicError] = useState('');
    const [userInfo, setUserInfo] = useState(null);

    // Cosmetic editing state
    const [selectedParticles, setSelectedParticles] = useState('none');
    const [cardSpecial, setCardSpecial] = useState('none');
    const [cardCustomColor1, setCardCustomColor1] = useState('#e05c00');
    const [cardCustomColor2, setCardCustomColor2] = useState('#1a0050');
    const [cardAnimMode, setCardAnimMode] = useState('static');
    const [nameSpecial, setNameSpecial] = useState('default');
    const [nameCustomColor1, setNameCustomColor1] = useState('#e05c00');
    const [nameCustomColor2, setNameCustomColor2] = useState('#ffd200');
    const [nameAnimMode, setNameAnimMode] = useState('static');
    const [tagline, setTagline] = useState('');
    const [bannerSpecial, setBannerSpecial] = useState('none');
    const [bannerColor1, setBannerColor1] = useState('');
    const [bannerColor2, setBannerColor2] = useState('');
    const [borderSpecial, setBorderSpecial] = useState('none');
    const [borderCustomColor1, setBorderCustomColor1] = useState('');
    const [borderCustomColor2, setBorderCustomColor2] = useState('');
    const [borderAnimMode, setBorderAnimMode] = useState('static');

    const [earnedKeys, setEarnedKeys] = useState(new Set());
    const [selectedBadges, setSelectedBadges] = useState([]);
    const [customSaveStatus, setCustomSaveStatus] = useState('');
    const [openSections, setOpenSections] = useState(new Set());

    const navigate = useNavigate();

    const applyCustomization = (c) => {
        if (!c) return;
        setSelectedParticles(c.particles || 'none');
        setCardSpecial(c.card_special || 'none');
        setCardCustomColor1(c.card_color1 || '#e05c00');
        setCardCustomColor2(c.card_color2 || '#1a0050');
        setCardAnimMode(c.card_anim || 'static');
        setNameSpecial(c.name_special || 'default');
        setNameCustomColor1(c.name_color1 || '#e05c00');
        setNameCustomColor2(c.name_color2 || '#ffd200');
        setNameAnimMode(c.name_anim || 'static');
        setTagline(c.tagline || '');
        setBannerSpecial(c.banner_special || 'none');
        setBannerColor1(c.banner_color1 || '');
        setBannerColor2(c.banner_color2 || '');
        setBorderSpecial(c.border_special || 'none');
        setBorderCustomColor1(c.border_color1 || '');
        setBorderCustomColor2(c.border_color2 || '');
        setBorderAnimMode(c.border_anim || 'static');
        setSelectedBadges(c.selected_badges || []);
    };

    useEffect(() => {
        document.body.classList.add("account-page-bg");

        const fetchData = async () => {
            try {
                setLoading(true);
                const token = localStorage.getItem('token');
                const userId = authData.user?.id;

                const [userResp, customResp] = await Promise.all([
                    axios.get(`${API_BASE}/data/get-user/${userId}`),
                    axios.get(`${API_BASE}/user/profile-customization`, {
                        headers: { Authorization: `Bearer ${token}` }
                    })
                ]);

                const u = userResp.data;
                setUserInfo({
                    id: u.id,
                    firstName: u.firstName,
                    lastName: u.lastName,
                    username: u.username,
                    profilePicture: u.profile_picture,
                    score: u.score ?? 0,
                    losses: u.losses ?? 0,
                    favoriteNFLTeam: u.favorite_nfl_team,
                    favoriteNBATeam: u.favorite_nba_team,
                    rank: u.rank,
                    badges: u.badges || [],
                });

                setEarnedKeys(new Set(customResp.data.earned_keys || []));
                applyCustomization(customResp.data.customization);
            } catch (error) {
                console.error('Error fetching data:', error);
            } finally {
                setLoading(false);
            }
        };

        if (authData.user?.id) fetchData();
        return () => document.body.classList.remove("account-page-bg");
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [authData.user?.id]);

    const syncLocal = (key, value) => {
        setUserInfo(prev => prev ? { ...prev, [key]: value } : prev);
    };

    const handleSaveCosmetics = async () => {
        const token = localStorage.getItem('token');
        setCustomSaveStatus('saving');
        try {
            const resp = await axios.post(`${API_BASE}/user/profile-customization`, {
                particles:       selectedParticles,
                card_special:    cardSpecial,
                card_color1:     cardCustomColor1,
                card_color2:     cardCustomColor2,
                card_anim:       cardAnimMode,
                name_special:    nameSpecial,
                name_color1:     nameCustomColor1,
                name_color2:     nameCustomColor2,
                name_anim:       nameAnimMode,
                tagline,
                banner_special:  bannerSpecial,
                banner_color1:   bannerColor1,
                banner_color2:   bannerColor2,
                border_special:  borderSpecial,
                border_color1:   borderCustomColor1,
                border_color2:   borderCustomColor2,
                border_anim:     borderAnimMode,
                selected_badges: selectedBadges,
            }, { headers: { Authorization: `Bearer ${token}` } });
            if (resp.data.customization) {
                setSelectedBadges(resp.data.customization.selected_badges || []);
            }
            setCustomSaveStatus('saved');
            setTimeout(() => navigate('/profile'), 800);
        } catch {
            setCustomSaveStatus('error');
            setTimeout(() => setCustomSaveStatus(''), 3000);
        }
    };

    const handleProfilePictureChange = async (e) => {
        const token = localStorage.getItem('token');
        const file = e.target.files[0];
        if (!file) return;
        const formData = new FormData();
        formData.append('profilePicture', file);
        try {
            const response = await axios.post(`${API_BASE}/user/update-profile-picture`, formData, {
                headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
            });
            updateUserAttribute('profile_picture', response.data.profile_picture);
            syncLocal('profilePicture', response.data.profile_picture);
            setProfilePicError('');
        } catch (error) {
            console.error('Error:', error.response?.data || error);
            setProfilePicError('Failed to update profile picture. Please try again.');
        }
    };

    const handleSelectFavoriteNFLTeam = async (e) => {
        const token = localStorage.getItem('token');
        const selectedTeam = e.target.value;
        syncLocal('favoriteNFLTeam', selectedTeam);
        try {
            const response = await axios.post(`${API_BASE}/user/update-favorite-nfl-team`, { selectedTeam }, {
                headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
            });
            if (response.status === 200) updateUserAttribute('favorite_nfl_team', response.data.favorite_nfl_team);
        } catch (error) {
            console.error('Error:', error.response?.data || error);
        }
    };

    const handleSelectFavoriteNBATeam = async (e) => {
        const token = localStorage.getItem('token');
        const selectedTeam = e.target.value;
        syncLocal('favoriteNBATeam', selectedTeam);
        try {
            const response = await axios.post(`${API_BASE}/user/update-favorite-nba-team`, { selectedTeam }, {
                headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
            });
            if (response.status === 200) updateUserAttribute('favorite_nba_team', response.data.favorite_nba_team);
        } catch (error) {
            console.error('Error:', error.response?.data || error);
        }
    };

    // Derive CSS classes for live preview
    const bannerCls = bannerSpecial !== 'none'
        ? ` ap-banner--${bannerSpecial}`
        : (bannerColor1 && earnedKeys.has('login_professional')) ? ' ap-banner--custom' : '';
    const cardCls = cardSpecial !== 'none'
        ? ` ap-header-card--${cardSpecial}`
        : cardAnimMode !== 'static' ? ` ap-header-card--custom-${cardAnimMode}` : '';
    const bannerStyle = bannerSpecial === 'none'
        ? { position: 'relative', overflow: 'hidden', '--banner-c1': bannerColor1, '--banner-c2': bannerColor2 }
        : { position: 'relative', overflow: 'hidden' };
    const ringCls = borderSpecial !== 'none'
        ? ` ap-avatar-ring--${borderSpecial}`
        : borderCustomColor1 ? ` ap-avatar-ring--custom-${borderAnimMode}` : '';
    const ringStyle = borderSpecial === 'none'
        ? {
            '--ring-c1': borderCustomColor1,
            ...(borderAnimMode === 'gradient' ? { '--ring-c2': borderCustomColor2 } : {}),
          }
        : {};
    const nameCls = nameSpecial !== 'default'
        ? ` ap-hero-name--${nameSpecial}`
        : nameAnimMode !== 'static' ? ` ap-hero-name--custom-${nameAnimMode}` : '';
    const nameStyle = nameSpecial === 'default'
        ? { '--name-c1': nameCustomColor1, '--name-c2': nameCustomColor2 }
        : {};

    const wins    = userInfo?.score  ?? 0;
    const losses  = userInfo?.losses ?? 0;
    const total   = wins + losses;
    const winRate = total > 0 ? Math.round((wins / total) * 100) : null;

    if (loading) {
        return (
            <BaseLayout>
                <FlameLoader text="Loading editor..." />
            </BaseLayout>
        );
    }

    if (!userInfo) {
        return (
            <BaseLayout>
                <p style={{ textAlign: 'center', color: 'var(--text-dim)', paddingTop: '3rem' }}>Could not load profile.</p>
            </BaseLayout>
        );
    }

    const has = (key) => earnedKeys.has(key);
    const maxBadges = authData.user?.role === 99 ? 2 : 3;

    const badgeAvailable = (b) => {
        if (b === '1st') return userInfo.rank === 1;
        if (b === '2nd') return userInfo.rank === 2;
        if (b === '3rd') return userInfo.rank === 3;
        const ach = BADGE_UNLOCK_ACH[b];
        return ach ? has(ach) : false;
    };

    const toggleBadge = (b) => {
        if (!badgeAvailable(b)) return;
        setSelectedBadges(prev => {
            if (prev.includes(b)) return prev.filter(x => x !== b);
            if (prev.length >= maxBadges) return prev;
            return [...prev, b];
        });
    };

    const toggleSection = (key) => setOpenSections(prev => {
        const next = new Set(prev);
        next.has(key) ? next.delete(key) : next.add(key);
        return next;
    });

    const lockedStyle = { opacity: 0.4, filter: 'grayscale(55%)', cursor: 'pointer', textDecoration: 'none' };

    const LockedPill = ({ achKey, label, themeCls }) => (
        <Link to={`/achievements/${achKey}`} className={`ap-pill${themeCls ? ' ' + themeCls : ''}`} style={lockedStyle} title={`Locked — earn ${ACH_TITLES[achKey] || achKey}`}>
            {label} <i className="fa fa-lock" style={{ fontSize: '0.6rem', marginLeft: 3 }} />
        </Link>
    );

    const LockedSwatch = ({ achKey, label, extraCls }) => (
        <Link to={`/achievements/${achKey}`} className={`ap-banner-swatch${extraCls ? ' ' + extraCls : ''}`} style={lockedStyle} title={`Locked — earn ${ACH_TITLES[achKey] || achKey}`}>
            {label} <i className="fa fa-lock" style={{ fontSize: '0.6rem', marginLeft: 3 }} />
        </Link>
    );

    const LockedColor = ({ achKey, label }) => (
        <Link to={`/achievements/${achKey}`} className="ap-color-label" style={{ ...lockedStyle, display: 'inline-flex', alignItems: 'center', gap: '6px' }} title={`Locked — earn ${ACH_TITLES[achKey] || achKey}`}>
            {label}
            <span style={{ display: 'inline-block', width: 28, height: 28, borderRadius: 4, background: '#555', border: '2px solid rgba(255,255,255,0.15)', position: 'relative', flexShrink: 0 }}>
                <i className="fa fa-lock" style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', fontSize: '0.6rem', color: 'rgba(255,255,255,0.6)' }} />
            </span>
        </Link>
    );

    const ALL_BADGE_KEYS = ['1st','2nd','3rd','founder','fire_lord','goat','legend','elite','flame_prince','popular','perfectionist','poop','all_rounder','traitor','friendly','nostalgic'];

    return (
        <BaseLayout>
            {/* ── Space atmosphere (preview) ── */}
            {selectedParticles === 'stars' && (
                <>
                    <div style={{ position: 'fixed', inset: 0, zIndex: 0, pointerEvents: 'none', background: 'radial-gradient(ellipse 90% 80% at 50% 40%, #020b1c 0%, #010510 50%, #000000 100%)' }} />
                    <div className="space-nebula-purple" style={{ position: 'fixed', inset: 0, zIndex: 0, pointerEvents: 'none', background: 'radial-gradient(ellipse 55% 45% at 25% 35%, rgba(90,0,180,0.28) 0%, rgba(50,0,120,0.1) 45%, transparent 68%)' }} />
                    <div className="space-nebula-blue" style={{ position: 'fixed', inset: 0, zIndex: 0, pointerEvents: 'none', background: 'radial-gradient(ellipse 50% 50% at 75% 65%, rgba(0,120,220,0.22) 0%, rgba(0,60,160,0.08) 45%, transparent 68%)' }} />
                    <div style={{ position: 'fixed', inset: 0, zIndex: 1, pointerEvents: 'none' }}><ParticlesBg preset="stars" /></div>
                    <div className="space-shooting-star space-shooting-star-1" style={{ zIndex: 9999 }} />
                    <div className="space-shooting-star space-shooting-star-2" style={{ zIndex: 9999 }} />
                    <div className="space-shooting-star space-shooting-star-3" style={{ zIndex: 9999 }} />
                    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, height: '25vh', zIndex: 2, pointerEvents: 'none', background: 'linear-gradient(to bottom, rgba(0,0,5,0.85) 0%, transparent 100%)' }} />
                    <div style={{ position: 'fixed', bottom: 0, left: 0, right: 0, height: '20vh', zIndex: 2, pointerEvents: 'none', background: 'linear-gradient(to top, rgba(0,0,5,0.85) 0%, transparent 100%)' }} />
                    <div style={{ position: 'fixed', top: 0, left: 0, bottom: 0, width: '16vw', zIndex: 2, pointerEvents: 'none', background: 'linear-gradient(to right, rgba(0,0,8,0.78) 0%, transparent 100%)' }} />
                    <div style={{ position: 'fixed', top: 0, right: 0, bottom: 0, width: '16vw', zIndex: 2, pointerEvents: 'none', background: 'linear-gradient(to left, rgba(0,0,8,0.78) 0%, transparent 100%)' }} />
                </>
            )}
            {/* ── Fire atmosphere (preview) ── */}
            {selectedParticles === 'fire' && (
                <>
                    <div style={{ position: 'fixed', inset: 0, zIndex: 0, pointerEvents: 'none', background: 'radial-gradient(ellipse 80% 40% at 50% 115%, rgba(160,20,0,0.3) 0%, transparent 70%)' }} />
                    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, height: '30vh', zIndex: 0, pointerEvents: 'none', background: 'linear-gradient(to bottom, rgba(0,0,0,0.65) 0%, transparent 100%)' }} />
                    <div style={{ position: 'fixed', top: 0, left: 0, bottom: 0, width: '18vw', zIndex: 9999, pointerEvents: 'none', background: 'linear-gradient(to right, rgba(0,0,0,0.82) 0%, transparent 100%)' }} />
                    <div style={{ position: 'fixed', top: 0, right: 0, bottom: 0, width: '18vw', zIndex: 9999, pointerEvents: 'none', background: 'linear-gradient(to left, rgba(0,0,0,0.82) 0%, transparent 100%)' }} />
                    <div className="fire-bottom-glow" style={{ position: 'fixed', bottom: 0, left: 0, right: 0, height: '28vh', zIndex: 9999, pointerEvents: 'none', background: ['radial-gradient(ellipse 75% 90% at 50% 100%, rgba(255,100,0,0.42) 0%, rgba(200,40,0,0.18) 45%, transparent 72%)', 'linear-gradient(to top, rgba(3,0,0,0.97) 0%, rgba(3,0,0,0.65) 55%, transparent 100%)'].join(', '), animation: 'fire-bottom-pulse 3s ease-in-out infinite' }} />
                </>
            )}

            <div className="ap-page" style={{ position: 'relative', zIndex: 10 }}>
                {/* ── Top bar ── */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem', flexWrap: 'wrap', gap: '0.5rem' }}>
                    <button className="ap-friend-btn ap-friend-btn--muted" onClick={() => navigate('/profile')}>
                        <i className="fa fa-arrow-left" aria-hidden="true" /> Back to Profile
                    </button>
                    <span style={{ color: 'var(--text-dim)', fontSize: '0.8rem' }}>Changes are not saved until you click Save</span>
                </div>

                {/* ── Live preview header card ── */}
                <div className={`ap-header-card${bannerCls}${cardCls}`} style={bannerStyle}>
                    {bannerSpecial === 'heat' && [
                        { left: '5%',  w: '14%', skew: -12, delay: '0s',    dur: '3.4s'  },
                        { left: '16%', w: '10%', skew:   6, delay: '0.65s', dur: '4.0s'  },
                        { left: '27%', w: '17%', skew:  -7, delay: '1.25s', dur: '3.1s'  },
                        { left: '40%', w: '11%', skew:  10, delay: '0.2s',  dur: '3.8s'  },
                        { left: '52%', w: '19%', skew:  -5, delay: '1.75s', dur: '2.85s' },
                        { left: '64%', w: '12%', skew:   8, delay: '0.9s',  dur: '4.15s' },
                        { left: '76%', w: '15%', skew:  -9, delay: '1.5s',  dur: '3.55s' },
                        { left: '87%', w:  '9%', skew:   4, delay: '0.4s',  dur: '3.25s' },
                    ].map((f, i) => (
                        <div key={i} className="ap-heat-flame" style={{ left: f.left, width: f.w, transform: `translateX(-50%) skewX(${f.skew}deg)`, animationDelay: f.delay, animationDuration: f.dur }} />
                    ))}
                    {selectedParticles !== 'none' && selectedParticles !== 'stars' && (
                        <div style={{ position: 'absolute', inset: 0, zIndex: 9, pointerEvents: 'none', borderRadius: 'inherit', overflow: 'hidden' }}>
                            <ParticlesBg preset={selectedParticles} />
                        </div>
                    )}
                    <div className="ap-header-card-inner">
                        <div className={`ap-avatar-ring${ringCls}`} style={ringStyle}>
                            <a href={userInfo.profilePicture || '/default.jpg'} target="_blank" rel="noopener noreferrer" title="View full image">
                                <img className="ap-avatar" src={userInfo.profilePicture || '/default.jpg'} alt={`${userInfo.firstName}'s profile`} />
                            </a>
                        </div>
                        <div className="ap-hero-info">
                            <p className={`ap-hero-name${nameCls}`} style={nameStyle}>{userInfo.firstName} {userInfo.lastName}</p>
                            {tagline && <p className="ap-tagline">{tagline}</p>}
                            {userInfo.username && <p className="ap-hero-username">@{userInfo.username}</p>}
                            {(selectedBadges.length > 0 || authData.user?.role === 99) && (
                                <div className="ap-badges">
                                    {(authData.user?.role === 99 ? ['admin', ...selectedBadges] : selectedBadges).map(key => {
                                        const meta = BADGE_META[key];
                                        if (!meta) return null;
                                        return <span key={key} className={`badge ${meta.cls}`}>{meta.label}</span>;
                                    })}
                                </div>
                            )}
                            <div className="ap-stats-row">
                                <div className="ap-stat-tile">
                                    <div className="ap-stat-value green">{wins}</div>
                                    <div className="ap-stat-label">Wins</div>
                                </div>
                                <div className="ap-stat-tile">
                                    <div className="ap-stat-value red">{losses}</div>
                                    <div className="ap-stat-label">Losses</div>
                                </div>
                                <div className="ap-stat-tile ap-stat-tile--winrate">
                                    <div className="ap-stat-value">{winRate !== null ? `${winRate}%` : '—'}</div>
                                    <div className="ap-stat-label">Win Rate</div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* ── Edit controls ── */}
                <div className="ap-card">
                    <p className="ap-card-title">Edit Profile</p>
                    {profilePicError && <p className="ap-error">{profilePicError}</p>}
                    <div className="ap-edit-grid">
                        <div>
                            <input
                                type="file"
                                accept="image/*"
                                id="ep-photo-input"
                                onChange={handleProfilePictureChange}
                                style={{ display: 'none' }}
                            />
                            <label htmlFor="ep-photo-input" className="ap-upload-label">
                                <i className="fa fa-camera" aria-hidden="true" /> Change Photo
                            </label>
                        </div>
                        <select className="ap-select" onChange={handleSelectFavoriteNFLTeam} value={userInfo.favoriteNFLTeam || ''}>
                            <option value="" disabled>Favorite NFL Team</option>
                            {NFL_TEAMS.map(t => <option key={t} value={t}>{t}</option>)}
                        </select>
                        <select className="ap-select" onChange={handleSelectFavoriteNBATeam} value={userInfo.favoriteNBATeam || ''}>
                            <option value="" disabled>Favorite NBA Team</option>
                            {NBA_TEAMS.map(t => <option key={t} value={t}>{t}</option>)}
                        </select>
                    </div>

                    {/* ── Badges ── */}
                    <div className="ap-border-section">
                        <button className="ap-border-section-label ap-section-toggle" onClick={() => toggleSection('badges')}>
                            Badges
                            <span className="ap-cosmetics-count"> {selectedBadges.length}/{maxBadges} selected</span>
                            <i className={`fa fa-chevron-down ap-section-chevron${openSections.has('badges') ? ' ap-section-chevron--open' : ''}`} />
                        </button>
                        <SectionBody isOpen={openSections.has('badges')}>
                            <p className="ap-cosmetics-hint">Click an unlocked badge to add or remove it. Up to {maxBadges} shown on your profile{authData.user?.role === 99 ? ' (Admin badge always occupies 1 slot)' : ''}.</p>
                            <div className="ap-badge-picker">
                                {authData.user?.role === 99 && (
                                    <span className="badge badge-admin ap-badge-picker-item ap-badge-picker-item--always-on" title="Always displayed — cannot remove">
                                        Admin <i className="fa fa-lock" style={{ fontSize: '0.65rem', marginLeft: 3 }} />
                                    </span>
                                )}
                                {ALL_BADGE_KEYS.map(b => {
                                    const meta = BADGE_META[b];
                                    if (!meta) return null;
                                    const avail    = badgeAvailable(b);
                                    const selected = selectedBadges.includes(b);
                                    const achKey   = BADGE_UNLOCK_ACH[b];
                                    if (!avail) {
                                        const linkKey = achKey || (b === '1st' ? 'leaderboard_top1' : null);
                                        return (
                                            <Link
                                                key={b}
                                                to={linkKey ? `/achievements/${linkKey}` : '/leaderboard'}
                                                className={`badge ${meta.cls} ap-badge-picker-item ap-badge-picker-item--locked`}
                                                title={linkKey ? `Locked — earn ${ACH_TITLES[linkKey] || linkKey}` : 'Requires leaderboard rank'}
                                            >
                                                {meta.label} <i className="fa fa-lock" style={{ fontSize: '0.65rem', marginLeft: 3 }} />
                                            </Link>
                                        );
                                    }
                                    return (
                                        <button
                                            key={b}
                                            className={`badge ${meta.cls} ap-badge-picker-item${selected ? ' ap-badge-picker-item--selected' : ''}`}
                                            onClick={() => toggleBadge(b)}
                                            title={selected ? 'Click to remove' : (selectedBadges.length >= maxBadges ? 'Remove a badge first' : 'Click to add')}
                                            disabled={!selected && selectedBadges.length >= maxBadges}
                                        >
                                            {meta.label}
                                            {selected && <i className="fa fa-check" style={{ fontSize: '0.65rem', marginLeft: 4 }} />}
                                        </button>
                                    );
                                })}
                            </div>
                        </SectionBody>
                    </div>

                    {/* ── Card Particles ── */}
                    <div className="ap-border-section">
                        <button className="ap-border-section-label ap-section-toggle" onClick={() => toggleSection('particles')}>
                            Card Particles
                            <i className={`fa fa-chevron-down ap-section-chevron${openSections.has('particles') ? ' ap-section-chevron--open' : ''}`} />
                        </button>
                        <SectionBody isOpen={openSections.has('particles')}><div className="ap-banner-picker">
                            <button className={`ap-banner-swatch ap-particle-swatch--none${selectedParticles === 'none' ? ' ap-banner-swatch--selected' : ''}`} onClick={() => setSelectedParticles('none')}>None</button>
                            {has('on_fire_4') ? (
                                <button className={`ap-banner-swatch ap-particle-swatch--fire${selectedParticles === 'fire' ? ' ap-banner-swatch--selected' : ''}`} onClick={() => setSelectedParticles('fire')}>Fire</button>
                            ) : (
                                <LockedSwatch achKey="on_fire_4" label="Fire" extraCls="ap-particle-swatch--fire" />
                            )}
                            {has('flame_picker_4') ? (
                                <button className={`ap-banner-swatch ap-particle-swatch--stars${selectedParticles === 'stars' ? ' ap-banner-swatch--selected' : ''}`} onClick={() => setSelectedParticles('stars')}>Stars</button>
                            ) : (
                                <LockedSwatch achKey="flame_picker_4" label="Stars" extraCls="ap-particle-swatch--stars" />
                            )}
                            {/* <span className="ap-lock-pill ap-lock-pill--tbd"><i className="fa fa-lock" /> Confetti — Coming Soon</span> */}
                        </div></SectionBody>
                    </div>

                    {/* ── Card Effect ── */}
                    <div className="ap-border-section">
                        <button className="ap-border-section-label ap-section-toggle" onClick={() => toggleSection('card-effect')}>
                            Card Effect
                            <i className={`fa fa-chevron-down ap-section-chevron${openSections.has('card-effect') ? ' ap-section-chevron--open' : ''}`} />
                        </button>
                        <SectionBody isOpen={openSections.has('card-effect')}><p className="ap-cosmetics-sub">Special</p>
                            <div className="ap-custom-row" style={{ marginBottom: '0.75rem' }}>
                                <button className={`ap-pill${cardSpecial === 'none' ? ' ap-pill--active' : ''}`} onClick={() => setCardSpecial('none')}>None</button>
                                {has('wins_100') ? (
                                    <button className={`ap-pill ap-pill--neon${cardSpecial === 'neon' ? ' ap-pill--active' : ''}`} onClick={() => setCardSpecial('neon')}>RGB</button>
                                ) : <LockedPill achKey="wins_100" label="RGB" themeCls="ap-pill--neon" />}
                                {has('leaderboard_top1') ? (
                                    <button className={`ap-pill ap-pill--gold-shimmer${cardSpecial === 'gold-shimmer' ? ' ap-pill--active' : ''}`} onClick={() => setCardSpecial('gold-shimmer')}>Gold</button>
                                ) : <LockedPill achKey="leaderboard_top1" label="Gold" themeCls="ap-pill--gold-shimmer" />}
                            </div>
                            {cardSpecial === 'none' && (<>
                                <hr className="ap-section-divider" />
                                <p className="ap-cosmetics-sub">Color</p>
                                <div className="ap-custom-row" style={{ marginBottom: '0.75rem' }}>
                                    {has('login_novice') ? (
                                        <label className="ap-color-label">Color 1<input type="color" value={cardCustomColor1} onChange={e => setCardCustomColor1(e.target.value)} /></label>
                                    ) : <LockedColor achKey="login_novice" label="Color 1" />}
                                    {has('login_amateur') ? (
                                        <label className="ap-color-label">Color 2<input type="color" value={cardCustomColor2} onChange={e => setCardCustomColor2(e.target.value)} /></label>
                                    ) : <LockedColor achKey="login_amateur" label="Color 2" />}
                                </div>
                                <p className="ap-cosmetics-sub">Animation</p>
                                <div className="ap-custom-row">
                                    <button className={`ap-pill${cardAnimMode === 'static' ? ' ap-pill--active' : ''}`} onClick={() => setCardAnimMode('static')}>Static</button>
                                    {has('login_amateur') ? (<>
                                        <button className={`ap-pill ap-pill--pulse${cardAnimMode === 'pulse' ? ' ap-pill--active' : ''}`} onClick={() => setCardAnimMode('pulse')}>Pulse</button>
                                        <button className={`ap-pill ap-pill--gradient${cardAnimMode === 'gradient' ? ' ap-pill--active' : ''}`} onClick={() => setCardAnimMode('gradient')}>Gradient</button>
                                    </>) : <><LockedPill achKey="login_amateur" label="Pulse" themeCls="ap-pill--pulse" /><LockedPill achKey="login_amateur" label="Gradient" themeCls="ap-pill--gradient" /></>}
                                </div>
                            </>)}
                        </SectionBody>
                    </div>

                    {/* ── Name Effect ── */}
                    <div className="ap-border-section">
                        <button className="ap-border-section-label ap-section-toggle" onClick={() => toggleSection('name-effect')}>
                            Name Effect
                            <i className={`fa fa-chevron-down ap-section-chevron${openSections.has('name-effect') ? ' ap-section-chevron--open' : ''}`} />
                        </button>
                        <SectionBody isOpen={openSections.has('name-effect')}><p className="ap-cosmetics-sub">Special</p>
                            <div className="ap-custom-row" style={{ marginBottom: '0.75rem' }}>
                                <button className={`ap-pill${nameSpecial === 'default' ? ' ap-pill--active' : ''}`} onClick={() => setNameSpecial('default')}>Default</button>
                                {has('leaderboard_top10') ? (
                                    <button className={`ap-pill ap-pill--glimmer${nameSpecial === 'glimmer' ? ' ap-pill--active' : ''}`} onClick={() => setNameSpecial('glimmer')}>Glimmer</button>
                                ) : <LockedPill achKey="leaderboard_top10" label="Glimmer" themeCls="ap-pill--glimmer" />}
                                {has('wins_100') ? (
                                    <button className={`ap-pill ap-pill--neon${nameSpecial === 'neon' ? ' ap-pill--active' : ''}`} onClick={() => setNameSpecial('neon')}>RGB</button>
                                ) : <LockedPill achKey="wins_100" label="RGB" themeCls="ap-pill--neon" />}
                                {has('leaderboard_top1') ? (
                                    <button className={`ap-pill ap-pill--gold${nameSpecial === 'gold' ? ' ap-pill--active' : ''}`} onClick={() => setNameSpecial('gold')}>Gold</button>
                                ) : <LockedPill achKey="leaderboard_top1" label="Gold" themeCls="ap-pill--gold" />}
                                {/* <span className="ap-lock-pill ap-lock-pill--tbd"><i className="fa fa-lock" /> Glitch</span> */}
                                {/* <span className="ap-lock-pill ap-lock-pill--tbd"><i className="fa fa-lock" /> Electric</span> */}
                            </div>
                            {nameSpecial === 'default' && (<>
                                <hr className="ap-section-divider" />
                                <p className="ap-cosmetics-sub">Color</p>
                                <div className="ap-custom-row" style={{ marginBottom: '0.75rem' }}>
                                    {has('flame_picker_2') ? (
                                        <label className="ap-color-label">Color 1<input type="color" value={nameCustomColor1} onChange={e => setNameCustomColor1(e.target.value)} /></label>
                                    ) : <LockedColor achKey="flame_picker_2" label="Color 1" />}
                                    {has('flame_picker_3') ? (
                                        <label className="ap-color-label">Color 2<input type="color" value={nameCustomColor2} onChange={e => setNameCustomColor2(e.target.value)} /></label>
                                    ) : <LockedColor achKey="flame_picker_3" label="Color 2" />}
                                </div>
                                <p className="ap-cosmetics-sub">Animation</p>
                                <div className="ap-custom-row">
                                    <button className={`ap-pill${nameAnimMode === 'static' ? ' ap-pill--active' : ''}`} onClick={() => setNameAnimMode('static')}>Static</button>
                                    {has('flame_picker_3') ? (<>
                                        <button className={`ap-pill ap-pill--pulse${nameAnimMode === 'pulse' ? ' ap-pill--active' : ''}`} onClick={() => setNameAnimMode('pulse')}>Pulse</button>
                                        <button className={`ap-pill ap-pill--gradient${nameAnimMode === 'gradient' ? ' ap-pill--active' : ''}`} onClick={() => setNameAnimMode('gradient')}>Gradient</button>
                                    </>) : <><LockedPill achKey="flame_picker_3" label="Pulse" themeCls="ap-pill--pulse" /><LockedPill achKey="flame_picker_3" label="Gradient" themeCls="ap-pill--gradient" /></>}
                                </div>
                            </>)}
                        </SectionBody>
                    </div>

                    {/* ── Tagline ── */}
                    <div className="ap-border-section">
                        {has('login_legend') ? (
                            <>
                                <button className="ap-border-section-label ap-section-toggle" onClick={() => toggleSection('tagline')}>
                                    Tagline
                                    <i className={`fa fa-chevron-down ap-section-chevron${openSections.has('tagline') ? ' ap-section-chevron--open' : ''}`} />
                                </button>
                                <SectionBody isOpen={openSections.has('tagline')}>
                                    <input
                                        className="ap-tagline-input"
                                        type="text"
                                        maxLength={40}
                                        placeholder='e.g. "Living on the Edge"'
                                        value={tagline}
                                        onChange={e => setTagline(e.target.value)}
                                    />
                                </SectionBody>
                            </>
                        ) : (
                            <Link to="/achievements/login_legend" style={{ textDecoration: 'none', display: 'flex', alignItems: 'center', justifyContent: 'space-between', opacity: 0.45, filter: 'grayscale(50%)', cursor: 'pointer' }} title="Locked — earn FP Legend">
                                <span className="ap-border-section-label" style={{ margin: 0, pointerEvents: 'none' }}>
                                    Tagline
                                </span>
                                <i className="fa fa-lock" style={{ color: 'var(--text-dim)', fontSize: '0.85rem' }} />
                            </Link>
                        )}
                    </div>

                    {/* ── Profile Banner ── */}
                    <div className="ap-border-section">
                        <button className="ap-border-section-label ap-section-toggle" onClick={() => toggleSection('banner')}>
                            Profile Banner
                            <i className={`fa fa-chevron-down ap-section-chevron${openSections.has('banner') ? ' ap-section-chevron--open' : ''}`} />
                        </button>
                        <SectionBody isOpen={openSections.has('banner')}><p className="ap-cosmetics-sub">Special</p>
                            <div className="ap-custom-row" style={{ marginBottom: '0.75rem' }}>
                                <button className={`ap-pill${bannerSpecial === 'none' ? ' ap-pill--active' : ''}`} onClick={() => setBannerSpecial('none')}>None</button>
                                {has('on_fire_3') ? (
                                    <button className={`ap-pill ap-pill--heat${bannerSpecial === 'heat' ? ' ap-pill--active' : ''}`} onClick={() => setBannerSpecial('heat')}>Heat</button>
                                ) : <LockedPill achKey="on_fire_3" label="Heat" themeCls="ap-pill--heat" />}
                                {has('wins_100') ? (
                                    <button className={`ap-pill ap-pill--cyber${bannerSpecial === 'neon' ? ' ap-pill--active' : ''}`} onClick={() => setBannerSpecial('neon')}>RGB</button>
                                ) : <LockedPill achKey="wins_100" label="RGB" themeCls="ap-pill--cyber" />}
                                {has('leaderboard_top1') ? (
                                    <button className={`ap-pill ap-pill--gold${bannerSpecial === 'gold' ? ' ap-pill--active' : ''}`} onClick={() => setBannerSpecial('gold')}>Gold</button>
                                ) : <LockedPill achKey="leaderboard_top1" label="Gold" themeCls="ap-pill--gold" />}
                            </div>
                            {bannerSpecial === 'none' && (<>
                                <hr className="ap-section-divider" />
                                {has('login_professional') ? (<>
                                    <p className="ap-cosmetics-sub">Colors <span style={{ fontSize: '0.68rem', color: 'var(--text-dim)' }}>(gradient animation always active)</span></p>
                                    <div className="ap-custom-row">
                                        <label className="ap-color-label">Color 1<input type="color" value={bannerColor1 || '#7c1800'} onChange={e => setBannerColor1(e.target.value)} /></label>
                                        <label className="ap-color-label">Color 2<input type="color" value={bannerColor2 || '#1a0050'} onChange={e => setBannerColor2(e.target.value)} /></label>
                                    </div>
                                </>) : (
                                    <div className="ap-custom-row">
                                        <LockedColor achKey="login_professional" label="Color 1" />
                                        <LockedColor achKey="login_professional" label="Color 2" />
                                    </div>
                                )}
                            </>)}
                        </SectionBody>
                    </div>

                    {/* ── PFP Border ── */}
                    <div className="ap-border-section">
                        <button className="ap-border-section-label ap-section-toggle" onClick={() => toggleSection('border')}>
                            PFP Border
                            <i className={`fa fa-chevron-down ap-section-chevron${openSections.has('border') ? ' ap-section-chevron--open' : ''}`} />
                        </button>
                        <SectionBody isOpen={openSections.has('border')}><p className="ap-cosmetics-sub">Special</p>
                            <div className="ap-custom-row" style={{ marginBottom: '0.75rem' }}>
                                <button className={`ap-pill${borderSpecial === 'none' ? ' ap-pill--active' : ''}`} onClick={() => setBorderSpecial('none')}>None</button>
                                {has('on_fire_2') ? (
                                    <button className={`ap-pill ap-pill--ember${borderSpecial === 'ember' ? ' ap-pill--active' : ''}`} onClick={() => setBorderSpecial('ember')}>Ember</button>
                                ) : <LockedPill achKey="on_fire_2" label="Ember" themeCls="ap-pill--ember" />}
                                {has('wins_100') ? (
                                    <button className={`ap-pill ap-pill--neon${borderSpecial === 'neon' ? ' ap-pill--active' : ''}`} onClick={() => setBorderSpecial('neon')}>RGB</button>
                                ) : <LockedPill achKey="wins_100" label="RGB" themeCls="ap-pill--neon" />}
                                {has('leaderboard_top1') ? (
                                    <button className={`ap-pill ap-pill--gold${borderSpecial === 'gold' ? ' ap-pill--active' : ''}`} onClick={() => setBorderSpecial('gold')}>Gold</button>
                                ) : <LockedPill achKey="leaderboard_top1" label="Gold" themeCls="ap-pill--gold" />}
                                {/* <span className="ap-lock-pill ap-lock-pill--tbd"><i className="fa fa-lock" /> Midnight</span> */}
                            </div>
                            {borderSpecial === 'none' && (<>
                                <hr className="ap-section-divider" />
                                <p className="ap-cosmetics-sub">Color</p>
                                <div className="ap-custom-row" style={{ marginBottom: '0.75rem' }}>
                                    {has('wins_10') ? (
                                        <label className="ap-color-label">Color 1<input type="color" value={borderCustomColor1} onChange={e => setBorderCustomColor1(e.target.value)} /></label>
                                    ) : <LockedColor achKey="wins_10" label="Color 1" />}
                                    {has('wins_50') ? (
                                        <label className="ap-color-label">Color 2<input type="color" value={borderCustomColor2} onChange={e => setBorderCustomColor2(e.target.value)} /></label>
                                    ) : <LockedColor achKey="wins_50" label="Color 2" />}
                                </div>
                                <p className="ap-cosmetics-sub">Animation</p>
                                <div className="ap-custom-row">
                                    <button className={`ap-pill${borderAnimMode === 'static' ? ' ap-pill--active' : ''}`} onClick={() => setBorderAnimMode('static')}>Static</button>
                                    {has('wins_50') ? (<>
                                        <button className={`ap-pill ap-pill--pulse${borderAnimMode === 'pulse' ? ' ap-pill--active' : ''}`} onClick={() => setBorderAnimMode('pulse')}>Pulse</button>
                                        <button className={`ap-pill ap-pill--gradient${borderAnimMode === 'gradient' ? ' ap-pill--active' : ''}`} onClick={() => setBorderAnimMode('gradient')}>Gradient</button>
                                    </>) : <><LockedPill achKey="wins_50" label="Pulse" themeCls="ap-pill--pulse" /><LockedPill achKey="wins_50" label="Gradient" themeCls="ap-pill--gradient" /></>}
                                </div>
                            </>)}
                        </SectionBody>
                    </div>

                    {/* ── Save button ── */}
                    <div className="ap-border-section" style={{ borderTop: 'none', paddingTop: 0 }}>
                        <button
                            className="ap-save-cosmetics-btn"
                            onClick={handleSaveCosmetics}
                            disabled={customSaveStatus === 'saving'}
                        >
                            {customSaveStatus === 'saving' && <><i className="fa fa-spinner fa-spin" /> Saving…</>}
                            {customSaveStatus === 'saved'  && <><i className="fa fa-check" /> Saved!</>}
                            {customSaveStatus === 'error'  && <><i className="fa fa-times" /> Error — try again</>}
                            {customSaveStatus === ''       && <><i className="fa fa-floppy-o" /> Save & Back to Profile</>}
                        </button>
                    </div>
                </div>
            </div>
        </BaseLayout>
    );
};

export default EditProfilePage;
